import { DischargeChecklistSummary } from 'models/DischargeChecklistItem';
import Review, {
  BaseReviewData,
  BaseReviewDataOptions,
  getDefaults as getReviewDefaults,
  parseProjectedDischarge,
  ReviewOptions,
  ReviewType,
} from 'models/Review';

type ProjectedDischargeData = {
  date: string;
  notificationSent: boolean;
  planningDischargeTo?: string | null;
};

export interface ProjectedDischargeOptions extends ReviewOptions {
  type: ReviewType.PROJECTED_DISCHARGE;
  data: Partial<ProjectedDischargeData & BaseReviewDataOptions>;
  // Sent only when asked for with the `reviews.discharge_checklist` include; items carry their notes
  // only with `reviews.discharge_checklist.notes`, which the patient story asks for.
  dischargeChecklist?: DischargeChecklistSummary | null;
}

export function getDefaults(): ProjectedDischargeOptions {
  const defaults = getReviewDefaults();

  return {
    ...defaults,
    type: ReviewType.PROJECTED_DISCHARGE,
    data: {
      ...defaults.data,
      date: '',
      notificationSent: false,
    },
  };
}

export default class ProjectedDischarge extends Review {
  declare type: ReviewType.PROJECTED_DISCHARGE;
  declare data: ProjectedDischargeData & BaseReviewData;
  dischargeChecklist: DischargeChecklistSummary | null;

  constructor(options: Partial<ProjectedDischargeOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    super(opts);
    this.dischargeChecklist = opts.dischargeChecklist ?? null;
  }

  static get type() {
    return ReviewType.PROJECTED_DISCHARGE;
  }

  get date() {
    return parseProjectedDischarge(this.data.date);
  }

  get planningDischargeTo() {
    return this.data.planningDischargeTo ?? null;
  }

  // A patient whose projected discharge predates the checklist, or whose client had nothing configured,
  // has none, and the card shows no checklist section.
  get hasDischargeChecklist() {
    return (this.dischargeChecklist?.items.length ?? 0) > 0;
  }
}
